import React, { useEffect, useRef } from 'react';
import {
  FaTimes,
  FaMapMarkerAlt,
  FaGlobe,
  FaCalendarAlt,
  FaCheckCircle,
  FaLaptopCode,
} from 'react-icons/fa';
import './InternshipDetailsModal.css';

function InternshipDetailsModal({ internship, onClose }) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!internship) return;

    // Prevent page scrolling while modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Handle ESC key press
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Focus close button on open
    if (modalRef.current) {
      const closeBtn = modalRef.current.querySelector('.modal-close-btn');
      if (closeBtn) closeBtn.focus();
    }

    // Restore scrolling and remove event listener on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [internship, onClose]);

  if (!internship) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const companyName = internship.companyName || internship.company;
  const logo = internship.logo || internship.image;
  const mode = internship.mode || internship.type;
  const location = internship.location || internship.city;
  const responsibilities = internship.responsibilities || [];
  const technologies = internship.technologies || [];

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="internship-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="internship-modal-title"
      >
        <button
          type="button"
          className="modal-close-btn"
          onClick={onClose}
          aria-label="Close modal"
        >
          <FaTimes />
        </button>

        <div className="modal-header">
          <div className="modal-logo-wrapper">
            <img src={logo} alt={companyName} className="modal-logo" />
          </div>
          <div className="modal-title-group">
            <h2 id="internship-modal-title" className="modal-company">
              {companyName}
            </h2>
            <h3 className="modal-role">{internship.role}</h3>
          </div>
        </div>

        <div className="modal-meta-grid">
          <div className="modal-meta-item">
            <FaMapMarkerAlt className="meta-icon location-icon" />
            <span className="meta-text">{location}</span>
          </div>

          <div className="modal-meta-item">
            <FaGlobe className="meta-icon mode-icon" />
            <span
              className={`modal-badge ${
                mode === 'Remote' ? 'badge-remote' : 'badge-offline'
              }`}
            >
              {mode}
            </span>
          </div>

          <div className="modal-meta-item">
            <FaCalendarAlt className="meta-icon duration-icon" />
            <span className="meta-text">{internship.duration}</span>
          </div>
        </div>

        <div className="modal-body">
          {/* Responsibilities Section */}
          <div className="modal-section">
            <h4 className="section-title">
              <FaCheckCircle className="section-icon" />
              Responsibilities
            </h4>
            {responsibilities.length > 0 ? (
              <ul className="modal-list">
                {responsibilities.map((resp, index) => (
                  <li key={index}>{resp}</li>
                ))}
              </ul>
            ) : (
              <p className="modal-placeholder-note">
                Specific responsibilities will be updated soon.
              </p>
            )}
          </div>

          {/* Technologies / Skills Section */}
          <div className="modal-section">
            <h4 className="section-title">
              <FaLaptopCode className="section-icon" />
              Technologies / Skills
            </h4>
            {technologies.length > 0 ? (
              <div className="modal-tech-tags">
                {technologies.map((tech, index) => (
                  <span key={index} className="tech-tag">
                    {tech}
                  </span>
                ))}
              </div>
            ) : (
              <p className="modal-placeholder-note">
                Key technologies will be updated soon.
              </p>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <a
            href={internship.certificateUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="modal-cert-btn"
            aria-label={`View certificate for ${companyName}`}
          >
            → View Certificate
          </a>
        </div>
      </div>
    </div>
  );
}

export default InternshipDetailsModal;
